'use client'

import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface EditableTitleProps {
  title: string
  onSave: (newTitle: string) => Promise<void> | void
  className?: string
  inputClassName?: string
  editButtonClassName?: string
  maxLength?: number
  triggerEdit?: boolean
  onEditTriggered?: () => void
}

export function EditableTitle({
  title,
  onSave,
  className,
  inputClassName,
  editButtonClassName,
  maxLength = 200,
  triggerEdit = false,
  onEditTriggered,
}: EditableTitleProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState(title)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditedTitle(title)
  }, [title])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Handle external trigger to enter edit mode
  useEffect(() => {
    if (triggerEdit && !isEditing) {
      if (title === 'New chat') {
        setEditedTitle('')
      }
      setIsEditing(true)
      onEditTriggered?.()
    }
  }, [triggerEdit, isEditing, title, onEditTriggered])

  const handleSave = async () => {
    const trimmed = editedTitle.trim()

    // If no title provided, use 'New chat' as fallback but don't save it if it's already the placeholder
    if (!trimmed) {
      setEditedTitle(title === 'New chat' ? 'New chat' : title)
      setIsEditing(false)
      return
    }

    // Don't save if the value hasn't changed
    if (trimmed === title) {
      setEditedTitle(title)
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(trimmed)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save title:', error)
      setEditedTitle(title)
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      setEditedTitle(title)
      setIsEditing(false)
    }
  }

  const handleBlur = () => {
    if (!isSaving) {
      handleSave()
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editedTitle}
        onChange={(e) => setEditedTitle(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        maxLength={maxLength}
        disabled={isSaving}
        className={cn(
          'min-w-0 max-w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm font-semibold text-gray-800 focus:border-gray-500 focus:outline-none',
          isSaving && 'cursor-wait opacity-50',
          inputClassName
        )}
      />
    )
  }

  return (
    <div className={cn('group flex min-w-0 items-center gap-1', className)}>
      <h2
        className={cn(
          'line-clamp-1 min-w-0 flex-1 overflow-hidden text-sm font-semibold',
          title === 'New chat' ? 'italic text-gray-500' : 'text-gray-800'
        )}
      >
        {title}
      </h2>
      <motion.button
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          // If it's the placeholder, clear it so user starts with empty input
          if (title === 'New chat') {
            setEditedTitle('')
          }
          setIsEditing(true)
        }}
        className={cn(
          'flex-shrink-0 overflow-hidden rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100',
          editButtonClassName
        )}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Edit title"
      >
        <Pencil className="h-3 w-3" />
      </motion.button>
    </div>
  )
}
